import {
  fetchQuestionHistory,
  saveQuestionHistory,
  deleteQuestionHistory,
  type AnalyticsQuestionHistoryEntry,
} from './questionHistory'

export type QuestionHistoryViewEntry = AnalyticsQuestionHistoryEntry &
  Readonly<{ pending?: boolean }>
type HistoryOperation = 'load' | 'save' | 'remove' | 'clear'
type HistoryFailure = {
  kind: HistoryOperation
  message: string
  retryLabel: string
  retry: () => void
}
type HistorySnapshot = {
  memberId: string | null
  entries: QuestionHistoryViewEntry[]
  fetching: boolean
  loading: boolean
  error: HistoryFailure | null
}
type HistorySession = {
  memberId: string
  controller: AbortController
  queue: Promise<void>
  pendingRequests: number
  pendingLoads: number
  pendingEntries: QuestionHistoryViewEntry[]
  entries: AnalyticsQuestionHistoryEntry[]
  error: HistoryFailure | null
  consumers: number
}

const EMPTY_SNAPSHOT: HistorySnapshot = {
  memberId: null,
  entries: [],
  fetching: false,
  loading: false,
  error: null,
}
let snapshot = EMPTY_SNAPSHOT
let activeSession: HistorySession | null = null
const listeners = new Set<() => void>()

export function subscribeToQuestionHistory(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
export const getQuestionHistorySnapshot = () => snapshot
export const getQuestionHistoryServerSnapshot = () => EMPTY_SNAPSHOT

function notify() {
  listeners.forEach((listener) => listener())
}
function isActive(session: HistorySession): boolean {
  return activeSession === session && !session.controller.signal.aborted
}
function publish(session: HistorySession) {
  if (!isActive(session)) return
  // Portal API controls saved-entry ordering, uniqueness, retention, and limits.
  // Pending rows are a temporary overlay; never truncate or filter a server response.
  snapshot = {
    memberId: session.memberId,
    entries: [...session.pendingEntries].reverse().concat(session.entries),
    fetching: session.pendingLoads > 0,
    loading: session.pendingRequests > 0,
    error: session.error,
  }
  notify()
}
function resetSession() {
  activeSession?.controller.abort()
  activeSession = null
  snapshot = EMPTY_SNAPSHOT
  notify()
}

function enqueue(
  session: HistorySession,
  kind: HistoryOperation,
  operation: (signal: AbortSignal) => Promise<AnalyticsQuestionHistoryEntry[]>,
  message: string,
  retryLabel: string,
  pendingEntry?: QuestionHistoryViewEntry,
) {
  if (!isActive(session)) return
  session.pendingRequests += 1
  if (kind === 'load') session.pendingLoads += 1
  // Keep one retry for the latest action; this popover is not a failed-operation queue.
  session.error = null
  if (pendingEntry) session.pendingEntries.push(pendingEntry)
  publish(session)
  // The API returns a complete list for every operation, so requests must run in order.
  session.queue = session.queue.then(async () => {
    if (!isActive(session)) return
    try {
      const entries = await operation(session.controller.signal)
      if (isActive(session)) {
        session.entries = entries
        session.error = null
      }
    } catch {
      if (isActive(session))
        session.error = {
          kind,
          message,
          retryLabel,
          retry: () => enqueue(session, kind, operation, message, retryLabel, pendingEntry),
        }
    } finally {
      session.pendingRequests -= 1
      if (kind === 'load') session.pendingLoads -= 1
      session.pendingEntries = session.pendingEntries.filter((entry) => entry !== pendingEntry)
      publish(session)
    }
  })
}

export function connectQuestionHistory(memberId: string | null): () => void {
  removeLegacyAnalyticsQuestionHistory()
  if (activeSession?.memberId !== memberId) resetSession()
  if (!memberId) return () => {}
  if (!activeSession) {
    activeSession = {
      memberId,
      controller: new AbortController(),
      queue: Promise.resolve(),
      pendingRequests: 0,
      pendingLoads: 0,
      pendingEntries: [],
      entries: [],
      error: null,
      consumers: 0,
    }
    reloadQuestionHistory(memberId)
  }
  const session = activeSession
  session.consumers += 1
  return () => {
    session.consumers -= 1
    // Immediate teardown also applies to Strict Mode's cleanup/reconnect cycle.
    // A load that already started may be canceled and fetched again; private-data
    // cleanup takes priority over suppressing development-only cancellation events.
    if (isActive(session) && session.consumers === 0) resetSession()
  }
}

function sessionFor(memberId: string | null) {
  return activeSession?.memberId === memberId ? activeSession : null
}
export function reloadQuestionHistory(memberId: string | null) {
  const session = sessionFor(memberId)
  if (session)
    enqueue(session, 'load', fetchQuestionHistory, 'Could not load history.', 'Reload history')
}
export function recordQuestionHistory(memberId: string | null, question: string) {
  const session = sessionFor(memberId)
  const trimmed = question.trim()
  if (!session || !trimmed) return
  enqueue(
    session,
    'save',
    (signal) => saveQuestionHistory(trimmed, signal),
    'Could not save this question to history.',
    'Retry save',
    { id: crypto.randomUUID(), question: trimmed, askedAt: Date.now(), pending: true },
  )
}
export function removeQuestionHistory(memberId: string | null, entry: QuestionHistoryViewEntry) {
  const session = sessionFor(memberId)
  // A pending row has no server id yet. It can be selected, but not deleted.
  if (session && !entry.pending)
    enqueue(
      session,
      'remove',
      (signal) => deleteQuestionHistory(entry.id, signal),
      'Could not remove the question.',
      'Retry remove',
    )
}
export function clearQuestionHistory(memberId: string | null) {
  const session = sessionFor(memberId)
  if (session)
    enqueue(
      session,
      'clear',
      (signal) => deleteQuestionHistory(undefined, signal),
      'Could not clear history.',
      'Retry clear',
    )
}
export function clearAllAnalyticsQuestionHistory(): void {
  resetSession()
  removeLegacyAnalyticsQuestionHistory()
}

const ANALYTICS_QUESTION_HISTORY_STORAGE_KEY_PREFIX = 'portals:analytics-question-history'

// Intentional cutover: discard legacy browser history rather than POST it as new asks.
// Legacy rows have only a member-scoped browser key; the API scopes history by
// authenticated user + organization and assigns asked_at on every POST. Import
// needs a separate contract to preserve identity, age, and existing server history.
export function removeLegacyAnalyticsQuestionHistory(): void {
  if (typeof window === 'undefined') return

  try {
    const keysToRemove: string[] = []
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index)
      if (key?.startsWith(`${ANALYTICS_QUESTION_HISTORY_STORAGE_KEY_PREFIX}:`)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((key) => {
      window.localStorage.removeItem(key)
    })
  } catch {
    // Browser storage can be unavailable. Logout and session expiry still continue.
  }
}
